"use client";

import { useState } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";

const plans = [
  {
    id: "free",
    name: "Digital Preview",
    tagline: "Try Before You Tag",
    monthlyPrice: 0,
    annualPrice: 0,
    cta: "Start Free Preview",
    ctaHref: "/checkout?plan=free",
    popular: false,
    accent: "border-white/8",
    accentHover: "hover:border-white/20",
    ctaStyle: "border border-white/20 hover:border-white/40 text-white hover:bg-white/5",
    features: [
      { text: "5 items (digital only)", included: true },
      { text: "All 5 viewer templates", included: true },
      { text: "Interactive markers", included: true },
      { text: "Public profile page", included: true },
      { text: "Cloud storage (500 MB)", included: true },
      { text: "Test before buying tags", included: true },
      { text: "NFC tag activation", included: false },
      { text: "Remove branding", included: false },
      { text: "Priority support", included: false },
    ],
  },
  {
    id: "starter-kit",
    name: "Starter Kit",
    tagline: "Get Started with Hardware",
    oneTimePrice: 49,
    thenMonthlyPrice: 9,
    description: "One-time purchase includes 5 NFC tags + first month of Starter subscription",
    cta: "Order Starter Kit",
    ctaHref: "/checkout?plan=starter-kit",
    popular: true,
    accent: "border-accent-crimson/40",
    accentHover: "hover:border-accent-crimson/60",
    ctaStyle: "bg-accent-crimson hover:bg-accent-crimson-hover text-white btn-lift",
    features: [
      { text: "5 premium NFC tags (hardware)", included: true },
      { text: "First month included", included: true },
      { text: "Then $9/mo to stay active", included: true },
      { text: "25 active tags max", included: true },
      { text: "100 items capacity", included: true },
      { text: "All viewer templates", included: true },
      { text: "Interactive markers", included: true },
      { text: "Additional tags $4 each", included: true },
      { text: "Remove branding", included: false },
    ],
  },
  {
    id: "starter",
    name: "Starter",
    tagline: "Monthly Subscription",
    monthlyPrice: 9,
    annualPrice: 7.50,
    description: "Requires Starter Kit purchase or existing tags",
    cta: "Subscribe Monthly",
    ctaHref: "/checkout?plan=starter",
    popular: false,
    accent: "border-white/8",
    accentHover: "hover:border-white/20",
    ctaStyle: "border border-white/20 hover:border-white/40 text-white hover:bg-white/5",
    features: [
      { text: "25 active NFC tags", included: true },
      { text: "100 items capacity", included: true },
      { text: "All viewer templates", included: true },
      { text: "Interactive markers", included: true },
      { text: "Public profile page", included: true },
      { text: "Standard support", included: true },
      { text: "Additional tags $4 each", included: true },
      { text: "Remove branding", included: false },
      { text: "API access", included: false },
    ],
  },
  {
    id: "pro",
    name: "Pro",
    tagline: "For Serious Collectors",
    monthlyPrice: 19,
    annualPrice: 15,
    cta: "Go Pro",
    ctaHref: "/checkout?plan=pro",
    popular: false,
    accent: "border-accent-gold/20",
    accentHover: "hover:border-accent-gold/40",
    ctaStyle: "border border-accent-gold/30 hover:border-accent-gold/60 text-accent-gold hover:bg-accent-gold/5",
    features: [
      { text: "100 active NFC tags", included: true },
      { text: "Unlimited items", included: true },
      { text: "All viewer templates", included: true },
      { text: "Interactive markers", included: true },
      { text: "Remove branding", included: true },
      { text: "Custom URL slug", included: true },
      { text: "Priority support", included: true },
      { text: "API access", included: true },
      { text: "Additional tags $3 each", included: true },
    ],
  },
  {
    id: "collector",
    name: "Collector",
    tagline: "Unlimited Everything",
    monthlyPrice: 49,
    annualPrice: 39,
    cta: "Go Unlimited",
    ctaHref: "/checkout?plan=collector",
    popular: false,
    accent: "border-accent-purple/20",
    accentHover: "hover:border-accent-purple/40",
    ctaStyle: "border border-accent-purple/30 hover:border-accent-purple/60 text-accent-purple hover:bg-accent-purple/5",
    features: [
      { text: "Unlimited active tags", included: true },
      { text: "Unlimited items", included: true },
      { text: "White label option", included: true },
      { text: "Custom domain", included: true },
      { text: "Dedicated support", included: true },
      { text: "Multi-user accounts", included: true },
      { text: "API access", included: true },
      { text: "Bulk tag pricing ($2 each)", included: true },
      { text: "Perfect for stores/museums", included: true },
    ],
  },
];

export default function PricingCards() {
  const [annual, setAnnual] = useState(false);

  return (
    <section className="py-16 px-6 md:px-8 bg-bg-primary" aria-label="Pricing plans">
      <div className="max-w-container mx-auto">
        {/* Toggle */}
        <div className="flex justify-center mb-12">
          <div className="flex items-center gap-4 bg-bg-card border border-white/8 rounded-full p-1.5">
            <button
              onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                !annual ? "bg-accent-crimson text-white" : "text-gray-500 hover:text-white"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                annual ? "bg-accent-crimson text-white" : "text-gray-500 hover:text-white"
              }`}
            >
              Annual
              <span className="font-mono text-[9px] bg-accent-gold/20 text-accent-gold px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {plans?.map((plan, i) => (
            <div
              key={plan?.id}
              className={`relative rounded-3xl border bg-bg-card p-7 lg:p-8 card-hover ${plan?.accent} ${plan?.accentHover} transition-all duration-300 ${
                plan?.popular ? "md:scale-105 glow-crimson" : ""
              } ${i === 1 ? "card-offset-shadow" : ""}`}
            >
              {/* Popular badge */}
              {plan?.popular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-accent-crimson text-white text-[10px] font-bold uppercase tracking-widest px-4 py-1 rounded-full">
                  Most Popular
                </div>
              )}

              {/* Plan name */}
              <div className="mb-6">
                <span className="font-mono text-[10px] text-gray-600 uppercase tracking-widest block mb-1">
                  {plan?.tagline}
                </span>
                <h3 className="font-serif text-2xl text-white">{plan?.name}</h3>
              </div>

              {/* Price */}
              <div className="mb-8 pb-8 border-b border-white/5">
                {plan?.id === "starter-kit" ? (
                  <div>
                    <div className="flex items-end gap-1 mb-2">
                      <span className="text-gray-500 text-lg self-start mt-2">$</span>
                      <span className="font-serif text-5xl text-white leading-none">
                        {plan?.oneTimePrice}
                      </span>
                      <span className="text-gray-500 text-sm mb-1">one-time</span>
                    </div>
                    <p className="text-gray-500 text-xs mb-1">
                      Includes 5 tags + first month
                    </p>
                    <p className="text-gray-400 text-sm font-medium">
                      Then ${plan?.thenMonthlyPrice}/mo
                    </p>
                  </div>
                ) : plan?.monthlyPrice === 0 ? (
                  <div>
                    <span className="font-serif text-5xl text-white">Free</span>
                    <p className="text-gray-500 text-xs mt-1">Forever</p>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-end gap-1">
                      <span className="text-gray-500 text-lg self-start mt-2">$</span>
                      <span className="font-serif text-5xl text-white leading-none">
                        {annual ? plan?.annualPrice : plan?.monthlyPrice}
                      </span>
                      <span className="text-gray-500 text-sm mb-1">/mo</span>
                    </div>
                    {annual && plan?.annualPrice && (
                      <p className="text-gray-600 text-xs mt-1">
                        Billed ${(plan?.annualPrice * 12)?.toFixed(0)}/year
                      </p>
                    )}
                  </div>
                )}
                {plan?.description && (
                  <p className="text-gray-600 text-xs mt-2">{plan?.description}</p>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-3.5 mb-8">
                {plan?.features?.map((feat) => (
                  <li key={feat?.text} className="flex items-center gap-3 text-sm">
                    <Icon
                      name={feat?.included ? "CheckCircleIcon" : "XCircleIcon"}
                      size={16}
                      className={feat?.included ? "text-accent-emerald flex-shrink-0" : "text-gray-700 flex-shrink-0"}
                    />
                    <span className={feat?.included ? "text-gray-300" : "text-gray-600"}>
                      {feat?.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link
                href={plan?.ctaHref}
                className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold text-sm transition-all duration-300 ${plan?.ctaStyle}`}
              >
                {plan?.cta}
                <Icon name="ArrowRightIcon" size={14} />
              </Link>
            </div>
          ))}
        </div>

        {/* Self-hosted callout */}
        <div className="mt-8 rounded-2xl border border-white/5 bg-bg-card p-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-accent-crimson/10 border border-accent-crimson/20 flex items-center justify-center flex-shrink-0">
              <Icon name="HomeIcon" size={18} className="text-accent-crimson" />
            </div>
            <div>
              <p className="text-white text-sm font-medium">Self-Hosted Option Available</p>
              <p className="text-gray-500 text-xs mt-0.5">
                Run TapTale on your own Raspberry Pi for complete control. No monthly fees, but you'll need to purchase NFC tags separately.
              </p>
            </div>
          </div>
          <Link
            href="/self-hosted"
            className="flex-shrink-0 inline-flex items-center gap-2 border border-white/15 hover:border-white/30 text-white text-sm font-medium px-5 py-2.5 rounded-full transition-all duration-300 hover:bg-white/5"
          >
            Learn More <Icon name="ArrowRightIcon" size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}